import React from 'react';
import './RegionOverlay.css';

export default function RegionOverlay({
  regions,
  selectedRegion,
  hoveredRegion,
  onRegionClick,
  onRegionHover,
  showRegions,
  getRegionColor,
  // Display-only mode for inactive pages in the continuous-scroll viewer: the
  // boxes render but capture no pointer events (clicks fall through to focus the
  // page) and wire up no click/hover handlers. Defaults false so the active-page
  // interactive usage is unchanged.
  readOnly = false,
}) {
  if (!showRegions || !regions.length) return null;

  return (
    <div className="region-overlay">
      {regions.map(region => {
        const color = getRegionColor(region.region_type);
        const isSelected = selectedRegion?.id === region.id;
        const isHovered = hoveredRegion?.id === region.id;

        // Position with percentages relative to the page-surface container.
        // .region-overlay fills that surface (width/height:100%), so 0-1
        // normalized region coords map straight to the displayed page —
        // resolution- and zoom-independent, matching ZoneEditorOverlay.
        // (Previously multiplied by canvas.width/height — the canvas BITMAP
        // resolution, not the displayed size — which pushed regions off the
        // page to the right whenever the two differed.)
        const style = {
          position: 'absolute',
          left: `${region.x * 100}%`,
          top: `${region.y * 100}%`,
          width: `${region.width * 100}%`,
          height: `${region.height * 100}%`,
          backgroundColor: color.bg,
          border: `2px ${isSelected || isHovered ? 'solid' : 'dashed'} ${color.border}`,
          borderRadius: '4px',
          pointerEvents: readOnly ? 'none' : 'all',
          cursor: readOnly ? 'default' : 'pointer',
          transition: 'all 0.2s',
          zIndex: isSelected ? 20 : isHovered ? 15 : 10,
          boxShadow: isSelected ? `0 0 0 4px ${color.border}33` : 'none',
        };

        return (
          <div
            key={region.id}
            className={`region-box ${isSelected ? 'region-box-selected' : ''} ${isHovered ? 'region-box-hovered' : ''}`}
            style={style}
            onClick={readOnly ? undefined : (e) => {
              e.stopPropagation();
              onRegionClick(region);
            }}
            onMouseEnter={readOnly ? undefined : () => onRegionHover(region)}
            onMouseLeave={readOnly ? undefined : () => onRegionHover(null)}
          >
            <div
              className="region-label-badge"
              style={{
                backgroundColor: color.border,
                opacity: isSelected || isHovered ? 1 : 0.8,
              }}
            >
              {color.icon} {region.label}
            </div>

            {(isSelected || isHovered) && region.confidence != null && (
              <div className="region-info-badge">
                {Math.round(region.confidence * 100)}% confidence
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
